import { SettingsManager } from "@viamap/viamap2-common";
import { EjendomTingbog } from "./DLLTypes";
import { removeXmlNsPrefix } from "src/functions/WFSutils";
import { EjendomTingbogWithUuid, parseTinglysningsDataAll, TinglysningsDataAllResponse } from "./parseTinglysningsDataXml";

export class tinglysningCollector {

   static async EjendomSoeg(EjendomSoegLookup: EjendomSoegLookup): Promise<any> {
      console.log(EjendomSoegLookup);
      const body = {
         ...EjendomSoegLookup,
         "format": "xml"
      }
      return fetchEtlService("ejendomSoeg", body).then((response) => {
         if (!response.ok) {
            throw new Error("Network response was not ok");
         }
         return response.json();
      }).then((body) => {
         let parser = new DOMParser();
         let xmlNoTags = removeXmlNsPrefix(body.body);
         return parser.parseFromString(xmlNoTags, "application/xml");
      }).catch((error) => {
         console.error("Error fetching EjendomSoeg:", error);
         throw new Error(error instanceof Error ? error.message : "Error fetching EjendomSoeg");
      });
   }

   static async EjendomSummariskPDF(matrikelListe: { matnr?: string | null, elav?: string | number | null }[]): Promise<Blob> {
      const normalized = matrikelListe.flatMap(({ matnr, elav }) => {
         const rawMat = matnr?.toString().trim() ?? "";
         const rawElav = elav == null ? "" : String(elav).replace(/\s+/g, "").trim();
         if (!rawMat || !rawElav) {
            return [];
         }
         const digits = rawMat.replace(/\D/g, "");
         const letter = rawMat.replace(/\d/g, "").trim();
         if (!digits) {
            return [];
         }
         return [{
            matnr: digits.padStart(4, "0") + letter,
            elav: rawElav,
         }];
      });
      if (!normalized.length) {
         throw new Error("Missing cadastral parcels for ejendom summarisk PDF");
      }
      const body = {
         "matrikelListe": normalized,
         "format": "pdf"
      }
      return fetchEtlService("getEjendomSummarisk", body).then((response) => {
         if (!response.ok) {
            throw new Error("Network response was not ok");
         }
         return response.json();
      }).then((body) => {
         const statusCode = Number(body?.statusCode);
         if (!Number.isNaN(statusCode) && statusCode >= 400) {
            throw new Error(
               (typeof body?.body === "string" && body.body.trim().slice(0, 400))
               || `Tinglysning gateway statusCode ${statusCode}`,
            );
         }
         const base64Data = body.body;
         if (!base64Data || typeof base64Data !== "string") {
            throw new Error("PDF data not found in API response");
         }
         const binaryData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
         return new Blob([binaryData], { type: "application/pdf" });
      })
   }

   static async EjendomTingbog(BFENummer: string): Promise<EjendomTingbog> {
      const body = {
         "BFEnr": BFENummer
      }
      return fetchTL("getTinglysningsData", body);
   }

   static async EjendomTingbogAll(BFENummer: string): Promise<EjendomTingbogWithUuid[]> {
      const body = {
         "BFEnr": BFENummer
      }
      const response = await fetchTL("getTinglysningsDataAll", body) as TinglysningsDataAllResponse;
      return parseTinglysningsDataAll(response);
   }

   static async DokumentTingbog(uuid: string): Promise<XMLDocument> {
      const body = {
         "uuid": uuid
      }
      let reps = await fetchTL("getTinglysningsDokument", body);
      try {
         let parser = new DOMParser();
         let xmlNoTags = removeXmlNsPrefix(reps);
         return parser.parseFromString(xmlNoTags, "application/xml");
      } catch (error: Error | any) {
         console.error("Error parsing XML:", error?.message ?? error);
         throw new Error("Expected XML response");
      }
   }

   static async fetchServitutBilag(bilagRef: string): Promise<{ data: Blob, name: string } | undefined> {
      const body = {
         "BilagDokumentId": bilagRef
      }
      return await fetchEtlService("getTinglysningsBilag", body).then((response) => {
         if (!response.ok) {
            throw new Error("Network response was not ok");
         }
         return response.json();
      }).then((body) => {
         const statusCode = Number(body?.statusCode);
         if (!Number.isNaN(statusCode) && statusCode >= 400) {
            throw new Error(body?.body || "Failed to fetch appendix data");
         }

         if (!body?.body || typeof body.body !== "string") {
            throw new Error("Invalid appendix response");
         }

         let xmlText = body.body;
         try {
            xmlText = JSON.parse(body.body);
         } catch {
            // Keep raw body when it is already plain XML.
         }

         let removedNsXml = removeXmlNsPrefix(xmlText);
         let xml = new DOMParser().parseFromString(removedNsXml, "application/xml");
         let name = xml.getElementsByTagName("Titel")[0]?.textContent || "bilag";
         let data = xml.getElementsByTagName("AttachmentBinaryData")[0];
         let mime = data?.getAttribute("mimetypeCode") || "application/pdf";
         let base64Data = data?.textContent || "";
         let blob = new Blob([Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))], { type: mime });
         return { data: blob, name: name + "." + mime.split("/").at(-1) };
      }).catch((error) => {
         console.error("Error fetching bilag:", error);
         throw new Error(error instanceof Error ? error.message : "Error fetching bilag");
      });
   }
}

async function fetchEtlService(endpoint: string, body: any): Promise<Response> {
   const baseUrl = SettingsManager.getSystemSetting("eTLServicesUrl");
   const url = baseUrl + "/" + endpoint;
   const API_KEY = SettingsManager.getSystemSetting("eTLServicesKey");

   return await fetch(url, {
      method: "POST",
      headers: {
         "Content-Type": "application/json",
         "x-api-key": API_KEY
      },
      body: JSON.stringify(body)
   })
}

/**
 * @deprecated Use fetchEtlService instead for new endpoints, as the DLL services are being phased out.
 */
async function fetchTL(endpoint: string, body: any): Promise<any> {
   const baseUrl = SettingsManager.getSystemSetting("dllServicesUrl");
   const url = "https://" + baseUrl + "/" + endpoint;
   const API_KEY = SettingsManager.getSystemSetting("dllServicesKey");

   return await fetch(url, {
      method: "POST",
      headers: {
         "x-api-key": API_KEY ? API_KEY : "",
         "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
   }).then(async (response) => {
      if (!response.ok) {
         throw new Error("Network response was not ok");
      }
      const text = await response.text();
      const trimmed = text.trim();
      if (!trimmed) {
         return {};
      }
      try {
         return JSON.parse(trimmed);
      } catch {
         return text;
      }
   });
}

type EjendomSoegLookup = {
   dokumentAlias?: string;
   dokumentAliasHist?: string;
   dokumentID?: string;
   rettighedID?: string;
   side?: string;
};